import React from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle, FileCheck, FileText, Upload, X,
} from 'lucide-react';
import type { CourseOption, PendingUpload, PaperType } from './types';

interface Props {
  open: boolean;
  pending: PendingUpload[];
  courses: CourseOption[];
  onClose: () => void;
  onClear: () => void;
  onSubmit: () => void;
  onRemove: (id: string) => void;
  onPatch: (id: string, patch: Partial<PendingUpload>) => void;
}

const PAPER_TYPES: PaperType[] = ['Final', 'CAT', 'Re-sit', 'Mock', 'Other'];

/**
 * Bottom slide-up sheet listing pending uploads with their per-file
 * metadata form. Portal-mounted so it escapes any transformed ancestor.
 */
export const PendingSheet: React.FC<Props> = ({
  open, pending, courses, onClose, onClear, onSubmit, onRemove, onPatch,
}) => {
  if (!open || typeof document === 'undefined') return null;

  const canSubmit = pending.length > 0 && pending.every((p) => !!p.course_id);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[10000] flex items-end justify-center bg-slate-900/45 backdrop-blur-sm animate-[ecFade_.2s_ease]"
      onClick={onClose}
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] max-h-[92vh] overflow-y-auto rounded-t-3xl bg-white shadow-[0_-20px_60px_rgba(0,0,0,0.2)] animate-[ecSlideUp_.25s_ease]"
        style={{ fontFamily: "'Afacad Flux', system-ui, sans-serif" }}
      >
        {/* Grabber */}
        <div className="mx-auto mt-2 mb-1 h-1.5 w-10 rounded-full bg-slate-300" />

        {/* Head */}
        <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="m-0 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#0e9172]">
              <Upload size={11} /> Ready to upload
            </p>
            <h3 className="m-0 mt-1 text-[22px] leading-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Tag and ship
            </h3>
            <p className="m-0 mt-0.5 text-[12px] text-slate-500">
              {pending.length} file{pending.length === 1 ? '' : 's'} · pick a course before submitting
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-900 hover:bg-slate-200"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="grid gap-3.5 px-4 py-4">
          {pending.map((p) => (
            <div key={p.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
              {/* File header */}
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#1a5276]/10 text-[#1a5276]">
                  <FileText size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="m-0 truncate text-[13px] font-bold text-slate-900">{p.file.name}</p>
                  <p className="m-0 mt-0.5 text-[11px] text-slate-500">
                    {(p.file.size / 1024 / 1024).toFixed(1)} MB
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(p.id)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Form */}
              <div className="mt-3 grid gap-2.5">
                <Field label="Course">
                  {courses.length > 0 ? (
                    <select
                      value={p.course_id || ''}
                      onChange={(e) => {
                        const c = courses.find((x) => x.id === e.target.value);
                        onPatch(p.id, { course_id: e.target.value, course_code: c?.code });
                      }}
                      className={selectClasses}
                    >
                      <option value="">Choose course…</option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>{c.code} — {c.title}</option>
                      ))}
                    </select>
                  ) : (
                    // Trainers don't have an LMS course list — accept
                    // a free-text course code that doubles as the id.
                    <input
                      type="text"
                      placeholder="e.g. COM 201S"
                      value={p.course_code || ''}
                      onChange={(e) => onPatch(p.id, {
                        course_code: e.target.value,
                        course_id: e.target.value.trim() || undefined,
                      })}
                      className={inputClasses}
                    />
                  )}
                </Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Year">
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder="2024"
                      value={p.year || ''}
                      onChange={(e) => onPatch(p.id, { year: e.target.value })}
                      className={inputClasses}
                    />
                  </Field>
                  <Field label="Paper type">
                    <select
                      value={p.paper_type || 'Final'}
                      onChange={(e) => onPatch(p.id, { paper_type: e.target.value as PaperType })}
                      className={selectClasses}
                    >
                      {PAPER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Academic session (optional)">
                  <input
                    type="text"
                    placeholder="e.g. May–Aug 2024"
                    value={p.academic_session || ''}
                    onChange={(e) => onPatch(p.id, { academic_session: e.target.value })}
                    className={inputClasses}
                  />
                </Field>
              </div>

              {/* Upload progress + error */}
              {p.progress > 0 && p.progress < 100 && (
                <div className="mt-2 h-1 overflow-hidden rounded-full bg-slate-200">
                  <span
                    className="block h-full rounded-full bg-gradient-to-r from-[#1a5276] to-[#0e9172] transition-all"
                    style={{ width: `${p.progress}%` }}
                  />
                </div>
              )}
              {p.error && (
                <p className="m-0 mt-2 flex items-center gap-1 text-[11px] text-[#c0392b]">
                  <AlertCircle size={11} /> {p.error}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Sticky actions */}
        <div
          className="sticky bottom-0 left-0 right-0 flex gap-2 border-t border-slate-100 bg-white px-4 py-3"
          style={{ paddingBottom: 'calc(12px + env(safe-area-inset-bottom))' }}
        >
          <button
            type="button"
            onClick={onClear}
            className="flex-1 rounded-xl bg-slate-100 px-4 py-3 text-[13px] font-bold text-slate-700 hover:bg-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-[#1a5276] px-4 py-3 text-[13px] font-bold text-white shadow-[0_6px_16px_rgba(26,82,118,0.25)] hover:bg-[#15436a] disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
          >
            <FileCheck size={13} /> Upload {pending.length}
          </button>
        </div>

        <style>{`
          @keyframes ecFade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes ecSlideUp { from { transform: translateY(20px); opacity: .9; } to { transform: translateY(0); opacity: 1; } }
        `}</style>
      </aside>
    </div>,
    document.body,
  );
};

/* ── Local atoms ───────────────────────────────────────────── */
const inputClasses =
  'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-900 outline-none focus:border-[#1a5276] focus:ring-2 focus:ring-[#1a5276]/15';

const selectClasses = inputClasses;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block">
    <span className="mb-1 block text-[10.5px] font-extrabold uppercase tracking-wider text-slate-500">
      {label}
    </span>
    {children}
  </label>
);
